import React, { useState } from "react";
import clsx from "clsx";

import IcnNotificationFill from "@assets/icons/rounded/notifications-fill.svg";
import { IconComponent, TextFieldComponent } from "@app/core";

import { BetterModal } from "@app/core/better-modal/better-modal";
import { useAppDispatch, useAppSelector } from "@app/redux/store";
import { authReducer } from "@app/redux/reducers/auth";
import { formatMessage } from "@app/translations/intl";
import { ClickableComponent } from "@app/core/clickable";
import { AnimatedEllipsisLoader } from "@app/core/animated-ellipsis-loader/animated-ellipsis-loader.component";

import { Authenticate } from "../auth";
import styles from "./login-modal.module.scss";

type MailSentStatus = "success" | "error" | "";

const LoginModal = () => {
  const { eanToLike, user } = useAppSelector(state => state.auth);
  const dispatch = useAppDispatch();
  const [mailSentStatus, setMailSentStatus] = useState<MailSentStatus>("");

  const handleClose = () => {
    dispatch(authReducer.setEanToLike(undefined));

    if (mailSentStatus === "error") {
      setMailSentStatus("");
    }
  };

  return (
    <BetterModal isOpen={Boolean(eanToLike && !user)} maxWidth={560} onClose={handleClose}>
      {mailSentStatus === "" ? (
        <LoginForm onStatusChange={setMailSentStatus} />
      ) : mailSentStatus === "success" ? (
        <div className={clsx(styles.content, styles.success)}>
          <IconComponent icon={IcnNotificationFill} size="40px" className={styles.icon} />
          <h2>{formatMessage({ id: "global.dealAlerts.modal.login.confirm.title" })}</h2>
          <p>{formatMessage({ id: "global.dealAlerts.modal.login.confirm.description" })}</p>
          <ClickableComponent
            buttonType="button"
            fullWidth
            variant="primary-2025-black"
            iconStyle="filled"
            title={formatMessage({ id: "global.dealAlerts.modal.closeButton.title" })}
            onClick={handleClose}
          />
        </div>
      ) : (
        <div className={clsx(styles.content, styles.error)}>
          <h2>{formatMessage({ id: "global.dealAlerts.modal.login.error.title" })}</h2>
          <p>{formatMessage({ id: "global.dealAlerts.modal.login.error.description" })}</p>
          <ClickableComponent
            buttonType="button"
            fullWidth
            variant="primary-2025-black"
            iconStyle="filled"
            title={formatMessage({ id: "global.dealAlerts.modal.closeButton.title" })}
            onClick={handleClose}
          />
        </div>
      )}
    </BetterModal>
  );
};

type LoginFormProps = {
  onStatusChange: (status: MailSentStatus) => void;
};

const LoginForm = ({ onStatusChange }: LoginFormProps) => {
  const [email, setEmail] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { platformId } = useAppSelector(state => state.settings);
  const { eanToLike } = useAppSelector(state => state.auth);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    setIsSubmitting(true);
    e.preventDefault();

    if (!email) {
      setIsSubmitting(false);

      return;
    }

    const result = await Authenticate.requestLogin(email, platformId, eanToLike);

    if (result) {
      onStatusChange("success");
      setEmail("");
      setIsSubmitting(false);
      return;
    }

    onStatusChange("error");
    setIsSubmitting(false);
  };

  return (
    <div className={clsx(styles.content, isSubmitting && styles.isLoading)}>
      <h2>{formatMessage({ id: "global.dealAlerts.modal.login.title" })}</h2>
      <p>{formatMessage({ id: "global.dealAlerts.modal.login.description" })}</p>

      <form className={styles.form} onSubmit={handleSubmit}>
        <TextFieldComponent
          id="emailAddress"
          type="email"
          placeholder={formatMessage({ id: "newsLetterModalForm.input.name.placeholder" })}
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={isSubmitting}
          required
        />
        <div className={styles.buttonContainer}>
          {isSubmitting && <AnimatedEllipsisLoader color="white" />}
          <ClickableComponent
            fullWidth
            size={44}
            variant="primary-2025-brand"
            iconStyle="filled"
            buttonType="submit"
            title={formatMessage({ id: "global.dealAlerts.modal.login.button.title" })}
            disabled={isSubmitting}
          />
        </div>
      </form>
    </div>
  );
};

export { LoginModal };
